import { useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise'
import { Check, Eye, EyeOff, Hourglass, Play, X } from 'lucide-react'
import { DateRange } from 'react-day-picker'

import { getT, getTMinus, toISO8601 } from '@fortem-monorepo/js-utilities'
import { CustomAgGrid, createColDefs } from '@fortem-monorepo/react-ag'
import { useResizableState } from '@fortem-monorepo/react-hooks'
import {
  HeaderConfigBar,
  Widget,
  WidgetContent,
  WidgetHeaderWithQuery,
  useApi,
} from '@fortem-monorepo/react-spa'
import {
  Badge,
  Button,
  DateRangePicker,
  LoadingButton,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  Spinner,
} from '@fortem-monorepo/react-ui'
import { Marketdata } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { RunMarketDataJobsModal } from '@/components/dialogs/RunMarketDataJobsModal'
import { INSTRUMENTS } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'

export interface MarketDataPageProps {}

export const MarketDataPage: React.FC<MarketDataPageProps> = ({}) => {
  const [dates, setDates] = useState<DateRange>({
    from: getTMinus(4),
    to: getT(),
  })

  const api = useApi()

  const [open, setOpen] = useState(false)

  const params: Marketdata.GetMarketdataPricesParams = {
    st_dt: dates.from ? toISO8601(dates.from) : '',
    ed_dt: dates.from ? toISO8601(dates.to ?? dates.from) : '',
  }

  const pricesQuery = useQuery({
    queryKey: ['marketdata', 'prices', params],
    queryFn: () => api.marketdata.getPrices(params),
    enabled: false,
  })

  const queueQuery = useQuery({
    queryKey: ['marketdata', 'queue'],
    queryFn: () => api.marketdata.getQueue(),
    refetchInterval: 10_000,
  })

  const pricesColDefs = useMemo(() => {
    return createPricesColDefs(pricesQuery.data ?? [])
  }, [pricesQuery.data])

  const queueColDefs = useMemo(() => {
    return createQueueColDefs(queueQuery.data ?? [])
  }, [queueQuery.data])

  const queueData = (queueQuery.data ?? []).map((data) => ({
    ...data,
    queued_at: toISO8601(new Date(data['queued_at'])),
  }))

  const [prices, queue] = useResizableState(2)

  return (
    <Page
      metadata={{ title: 'Marketdata Loader' }}
      permissions={INSTRUMENTS.MARKETDATA.permissions}
    >
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <DateRangePicker
            size="sm"
            date={{
              from: params.st_dt ? new Date(params.st_dt) : undefined,
              to: params.ed_dt ? new Date(params.ed_dt) : undefined,
            }}
            setDate={(dates) => {
              setDates({ from: dates.from, to: dates.to ?? dates.from })
            }}
          />
          <LoadingButton
            size="sm"
            variant={'outline'}
            onClick={() => pricesQuery.refetch()}
            icon={<Play />}
          >
            Get Prices
          </LoadingButton>
          <CanAccess permissions={[PERMISSIONS.RUN_MARKETDATA_JOBS]}>
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
              <Play />
              Run Jobs
            </Button>
          </CanAccess>
          <Button onClick={() => prices.toggle()} size="sm" variant="outline">
            Prices {prices.open ? <Eye /> : <EyeOff />}
          </Button>
          <Button onClick={() => queue.toggle()} size="sm" variant="outline">
            Queue {queue.open ? <Eye /> : <EyeOff />}
          </Button>
        </div>
      </HeaderConfigBar>
      <RunMarketDataJobsModal open={open} onOpenChange={setOpen} />
      <ResizablePanelGroup direction={'horizontal'} autoSaveId={'marketdata-panels'}>
        {prices.open && (
          <ResizablePanel id="marketdata-prices-panel" order={1}>
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery title="Marketdata Loader Instruments" query={pricesQuery} />
              <WidgetContent loading={pricesQuery.isLoading}>
                <CustomAgGrid
                  domLayout={'normal'}
                  rowData={pricesQuery.data}
                  columnDefs={pricesColDefs}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
        )}
        {prices.open && queue.open && <ResizableHandle />}
        {queue.open && (
          <ResizablePanel id="marketdata-queue-panel" order={2}>
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery title="Job Queue" query={queueQuery} />
              <WidgetContent loading={queueQuery.isLoading}>
                <CustomAgGrid
                  autoGroupColumnDef={{ headerName: 'Queued At' }}
                  domLayout={'normal'}
                  rowData={queueData}
                  columnDefs={queueColDefs}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
        )}
      </ResizablePanelGroup>
    </Page>
  )
}

const createQueueColDefs = (_data: Marketdata.GetMarketdataQueueRow[]) => {
  return createColDefs<Marketdata.GetMarketdataQueueRow>([
    { field: 'queued_at', headerName: 'Queued At', rowGroup: true, hide: true },
    { field: 'job_id', headerName: 'Job ID', hide: true },
    { field: 'for_manager_id', headerName: 'Manager ID', hide: true },
    { field: 'value_date', headerName: 'Value Date' },
    {
      field: 'status',
      headerName: 'Status',
      cellRenderer: (params: ICellRendererParams<Marketdata.GetMarketdataQueueRow>) => {
        return (
          <div className={'flex h-full items-center justify-center'}>
            {params.data?.status === 'queued' && (
              <Badge variant={'secondary'}>
                Queued <Hourglass />
              </Badge>
            )}
            {params.data?.status === 'running' && (
              <Badge theme={'warning'}>
                Running <Spinner />
              </Badge>
            )}
            {params.data?.status === 'cancelled' && <Badge theme={'destructive'}>Cancelled</Badge>}
            {params.data?.status === 'failed' && <Badge theme={'destructive'}>Failed</Badge>}
            {params.data?.status === 'succeeded' && (
              <Badge theme={'success'}>
                Completed <Check />
              </Badge>
            )}
            {params.data?.status === 'retrying' && (
              <Badge theme={'info'}>
                Retrying <Spinner />
              </Badge>
            )}
            {params.data?.status === 'skipped' && (
              <Badge theme={'info'}>
                Skipped <X />
              </Badge>
            )}
            {params.data?.status === 'stalled' && <Badge theme={'destructive'}>Stalled</Badge>}
          </div>
        )
      },
    },
    { field: 'query_name', headerName: 'Query Name' },
    { field: 'query_type', headerName: 'Query Type' },
    { field: 'created_by_name', headerName: 'Created By' },
    { field: 'started_at', headerName: 'Started At' },
    { field: 'completed_at', headerName: 'Completed At' },
    { field: 'error_message', headerName: 'Error Message' },
    { field: 'attempt_count', headerName: 'Attempt Count' },
    { field: 'max_attempts', headerName: 'Max Attempts' },
    { field: 'created_at', headerName: 'Created At', hide: true },
    { field: 'request_payload', headerName: 'Request Payload', hide: true },
    { field: 'request_payload_hash', headerName: 'Request Payload Hash', hide: true },
    { field: 'result_payload', headerName: 'Result Payload', hide: true },
    { field: 'external_request_id', headerName: 'External Request ID', hide: true },
    { field: 'processed_by_manager_id', headerName: 'Processed By Manager ID' },
    { field: 'error_type', headerName: 'Error Type', hide: true },
    { field: 'error_traceback', headerName: 'Error Traceback', hide: true },
    { field: 'updated_at', headerName: 'Updated At', hide: true },
  ])
}

const createPricesColDefs = (data: Marketdata.MarketDataPrices[]) => {
  return createColDefs<Marketdata.MarketDataPrices>([
    { field: 'instrument_id', hide: true },
    {
      headerName: 'Details',
      openByDefault: false,
      children: [
        { field: 'unique_identifier' },
        { field: 'org_display_name' },
        { field: 'provider_symbol', columnGroupShow: 'open' },
        { field: 'bbg_ticker', columnGroupShow: 'open' },
        { field: 'isin', columnGroupShow: 'open' },
        { field: 'short_name', columnGroupShow: 'open' },
      ],
    },
    ...Object.keys(data?.[0]?.dates ?? {})
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map((date) => {
        return {
          headerName: date,
          valueGetter: (params) => params.data?.dates[date].value,
          cellClassRules: {
            'bg-emerald-100': (params) => params.data?.dates[date].is_final === true,
            'bg-amber-100': (params) => params.data?.dates[date].is_final === false,
            'bg-red-100': (params) => params.data?.dates[date].is_final === null,
          },
        } satisfies ColDef<Marketdata.MarketDataPrices>
      }),
  ])
}
